import { CareEnvironment, CarePhase, FundingPolicyType } from '../../../../appModel';
import { appliedHealthCareInflation } from '../../../../appliedHealthCareInflation';
import {
  BuildYearSnapshotInput,
  FinalizeDeathBenefitInput,
  MonthComputeInput,
  PolicyProjectionStrategy,
} from '../strategyTypes';
import { DAYS_IN_MONTH } from '../sharedConstants';
import {
  computeMonthlyCostMetrics,
  computeNon2AdlDaysThisRow,
} from '../sharedEligibility';
import {
  consumeWaitingPeriodDaysForEnvironment,
  growthFactor,
  getCalendarPolicyYear,
  getCareCostAnchorYear,
  getDailyBenefitAmountForEnvironment,
  getFirstContributingPhaseStartYear,
  getPhaseCareMonthsByPhase,
  getMaxMonthlyBenefitAmount,
  getMonthlyBenefitAmountForEnvironment,
} from '../sharedPolicyConfig';
import {
  applyIndemnitySurplus,
  applyOpeningSurplusToCost,
} from '../sharedSurplus';
import { calculateClientAge } from '../clientAgeCalculation';
import { deriveStandardMilestones } from '../deriveMilestones';
import {
  deriveIneligibilityReason,
  deriveIneligibleDayBreakdown,
} from '../deriveIneligibilityReason';
import { buildPrePolicyMonth } from '../buildPrePolicyMonth';
import { deriveAtIssuePools, forwardBenefitPeriodTotal } from './durationPoolModel';

// ---------------------------------------------------------------------------
// Securian SecureCare III / IV — cash-indemnity asset-based hybrid life with an
// LTC acceleration + extension benefit. Single-life only (no joint variant).
//
// A dedicated split-out from `genericHybridStrategy`, combining the mechanics of
// its two closest parents:
//   - from NYL Asset Flex: DURATION-DERIVED pools (Monthly Benefit × benefit-
//     period months via `deriveAtIssuePools`), the Face opened from the stored
//     Face Amount, and PROPORTIONAL death-benefit depletion.
//   - from generic hybrid: ALWAYS-INDEMNITY payout with `indemnitySurplus`
//     accounting, and `growthFactor`-based inflation that honors SIMPLE or
//     COMPOUND (`isSimpleInflationProtection`).
//
// SecureCare runs through `genericHybridStrategy` today, so reusing `growthFactor`
// (not Asset Flex's compound-only `compoundFactor`) preserves current behavior
// for simple-inflation policies rather than regressing them.
//
// NOTE: This is Securian's SecureCare (`secureCareThree` / `secureCareFour`) —
// asset-based hybrid life. It is UNRELATED to New York Life's `nylSecureCare` /
// `nylJointSecureCare` (traditional standalone ShareCare LTC), which route to
// `traditionalLtcStrategy`.
//
// TWO POOLS, ONE RATE — Acceleration (base) drawn first, then Extension (eob):
// - Base / Acceleration pool = Monthly Benefit × acceleration duration months
//   (falls back to `policyMaximumBenefitAmount` when durations are absent).
// - Extension pool = Monthly Benefit × extension duration months (falls back to
//   `policyContinuationBenefitAmount`). LTC draws deplete Acceleration first;
//   once exhausted the draw falls to Extension.
// - Both pools inflate on the SINGLE `policyInflationProtection` rate at the
//   policy anniversary, capped at `policyInflationProtectionYears`, simple OR
//   compound. The Acceleration/Extension split is a drawdown-order diagram, not
//   two independently-inflating pools (unlike Asset Care Base/COB) — so the two
//   pool knobs carry the same rate. This models the illustration's single
//   uniformly-inflating "Total LTC Benefit" (field-mapping issue #3).
//
// PAYOUT — ALWAYS cash indemnity (p.1, p.6):
// - Pay the full pro-rated monthly cap as cash regardless of actual cost, drawn
//   base-then-Extension; a pool's last paying month may pay less than the cap
//   when it can't cover it. Cash above eligible cost accrues as `indemnitySurplus`
//   and drains on a later shortfall month (via `applyIndemnitySurplus`, the same
//   helper CM II and generic-hybrid indemnity use).
//
// STATIC DEATH BENEFIT, PROPORTIONAL DEPLETION:
// - The life Face is a FIXED amount (`policyMaximumBenefitAmount`); inflation
//   grows the LTC pools/cap, never the Face (`hasFlatDeathBenefit`).
// - Only Acceleration drawdown reduces the Face, and it does so IN PROPORTION to
//   the remaining Acceleration pool — not dollar-for-dollar. The un-inflated Face
//   ($52,167 × 2yr = $104,334 on the mapped illustration) is smaller than the
//   inflated 2-year acceleration draws ($105,899), so a dollar-for-dollar
//   reduction would floor the Face ~a year before the acceleration period ends.
//   Proportional instead tracks the fraction of the Acceleration pool consumed,
//   reaching the residual only as the face is (near-)fully accelerated (p.13: min
//   DB paid when the face is "fully accelerated"). Floored at
//   `policyMinimumGuaranteedBenefitAmount`. Extension drawdown never reduces it.
//   (Same rule as Asset Flex; at 0% inflation it coincides with dollar-for-dollar.)
//
// EP: standard two-tier (home/facility) WP with the standard 2-of-6-ADL gate.
// SecureCare IV reimburses the elimination period RETROACTIVELY; SecureCare III
// does not (III's days stay strictly out-of-pocket):
// - IV p.2: "Benefits for 90-day elimination period are paid retroactively."
// - IV p.7: "Your first LTC benefit on day 91 will include four monthly
//   payments: three payments for the 90-day elimination period and one payment
//   for your first month going forward."
// The III illustration's EP row carries no such language.
//
// This is a FIXED PRODUCT PROPERTY, gated on `policyType` rather than on the
// persisted `hasRetroactiveWaitingPeriodReimbursement` column — same treatment
// as always-indemnity below. That keeps already-uploaded SecureCare IV policies
// correct without a data backfill, and means the column is intentionally ignored
// for this product (it is not a user election).
//
// STRATEGY STATE carries `indemnitySurplus` and the once-per-claim `epReimbursed`
// latch; deltas surface the per-row accrual/application for the UI.
// ---------------------------------------------------------------------------

export interface SecureCareState {
  // Indemnity-surplus reserve: indemnity dollars paid above eligible cost,
  // carried forward and drained on a later shortfall month. SecureCare is always
  // indemnity, so — unlike a reimbursement generic-hybrid policy — this is a live
  // reserve, not a perpetual 0.
  indemnitySurplus: number;
  // True once the retroactive EP lump has fired. The EP is reimbursed once per
  // claim, so a second WP bucket satisfying later must not fire it again.
  epReimbursed: boolean;
  // Running count of 2-ADL-qualified days that were WITHHELD from payment across
  // the claim so far (the per-row active-environment WP consumption, summed).
  // This — not `initialWp − closeWp` — is what the retroactive lump reimburses:
  // the elimination period pays back days the insured actually went unpaid, and
  // a qualified day paid in full under a zero/already-satisfied co-environment is
  // not one of them (see `computeEpRetroactiveLump`). Frozen once the lump fires.
  epUnpaidWpDaysServed: number;
}

export interface SecureCareDeltas {
  indemnitySurplusAccrued: number;
  indemnitySurplusApplied: number;
  epLumpSumPaid: number;
  epLumpSumBasePaid: number;
  epLumpSumEobPaid: number;
  epLumpSumDays: number;
  epLumpSumDailyRate: number;
  epLumpSumAppliedToCost: number;
}

interface SecureCareInflation {
  // Single rate shared by both pools + the monthly cap. The Face never inflates.
  rate: number;
  capYears: number;
  isSimple: boolean;
}

function readInflation(ltcPolicy: PolicyFundingSource): SecureCareInflation {
  const rate = ltcPolicy.policyInflationProtection ?? 0;
  const capYearsRaw = ltcPolicy.policyInflationProtectionYears;
  return {
    rate,
    capYears: capYearsRaw && capYearsRaw > 0 ? capYearsRaw : 0,
    isSimple: ltcPolicy.isSimpleInflationProtection === true,
  };
}

// Surfaced on `terms.dataWarnings` for partially-migrated rows: the duration
// model is all-or-nothing (mirrors Asset Flex's P2-13), so a row with exactly
// one duration field stays on the stored dollar inputs and asks for a re-upload.
export const SECURE_CARE_MIXED_MODEL_WARNING =
  'This SecureCare policy has only one of the two LTC benefit duration fields, so the ' +
  'duration-derived pool model cannot be applied consistently. Both pools use the stored ' +
  'dollar amounts instead. Re-upload the illustration (or complete both duration fields) ' +
  'to enable the duration model.';

// Prior utilization drains the Acceleration (base) pool first; any excess spills
// into the Extension pool (mirrors nylAssetFlexStrategy). Both pools are taken at
// their pre-inflated claim-start values. Duplicated per strategy independence.
function splitPriorUtilization(
  utilizedToDate: number,
  baseAtClaimStart: number,
  eobAtClaimStart: number,
): { priorBaseUtilization: number; priorEobUtilization: number } {
  const priorBaseUtilization = Math.min(Math.max(utilizedToDate, 0), baseAtClaimStart);
  const priorEobUtilization = Math.min(
    Math.max(utilizedToDate - priorBaseUtilization, 0),
    eobAtClaimStart,
  );
  return { priorBaseUtilization, priorEobUtilization };
}

// Retroactive EP reimbursement is a SecureCare IV product feature; III pays
// nothing back for elimination-period days. See the header note.
function hasRetroactiveEliminationPeriod(ltcPolicy: PolicyFundingSource): boolean {
  return ltcPolicy.policyType === FundingPolicyType.secureCareFour;
}

interface EpLumpInputs {
  ltcPolicy: PolicyFundingSource;
  // WP balance for the active environment entering and leaving this row. Used
  // ONLY to detect the satisfaction edge (open > 0 → close 0) that fires the
  // lump; the reimbursed day count comes from `unpaidWpDaysServed`.
  openRelevantWp: number;
  closeRelevantWp: number;
  // Cumulative 2-ADL-qualified days that were WITHHELD from payment across the
  // claim, INCLUDING this row's withheld days. This is what the EP reimburses —
  // not `initialWp − closeWp`, which also counts days that burned the bucket
  // while being PAID under a zero/already-satisfied co-environment. On an
  // asymmetric claim (home 0 / facility 90) the paid home-care days decrement
  // the facility bucket too; reimbursing `initial − close` there pays those
  // already-paid days a second time.
  unpaidWpDaysServed: number;
  // Pool balances AFTER this row's regular draw — the lump draws from what is
  // left, base first then extension, exactly as a regular row does.
  baseAfterDraw: number;
  eobAfterDraw: number;
  baseMonthlyCap: number;
  eobMonthlyCap: number;
  alreadyReimbursed: boolean;
}

interface EpLumpResult {
  epLumpSumPaid: number;
  // Portion drawn from the Acceleration pool. Only this portion feeds the
  // PROPORTIONAL death-benefit reduction — CM II depletes its death benefit
  // dollar-for-dollar, which cannot be copied here without flooring the flat
  // Face early (see the header's proportional-depletion note).
  epLumpSumBasePaid: number;
  epLumpSumEobPaid: number;
  epLumpSumDays: number;
  epLumpSumDailyRate: number;
  fired: boolean;
}

// Ported from `cmTwoStrategy.computeEpRetroactiveLump`, which this follows
// closely. Three deliberate departures, each forced by the product rather than
// chosen:
//   1. No rider term. CM II pays base + rider and only the base portion depletes
//      its pool; SecureCare has a single monthly maximum.
//   2. Two pools. CM II caps the lump at its single base pool; SecureCare spills
//      Acceleration → Extension the same way a regular row does, so a claim that
//      opens with a partly-drawn Acceleration pool still gets the full lump.
//   3. The base portion is returned separately so the caller can run it through
//      proportional death-benefit depletion instead of CM II's dollar-for-dollar.
//
// Days funded are the days actually WITHHELD from payment across the claim
// (`unpaidWpDaysServed`, the running sum of each row's active-environment WP
// consumption), NOT `initialWp − closeWp`. The two coincide for the normal case
// — a policy entering the projection with its full EP outstanding and serving it
// in a single environment — giving 90 days × (monthly ÷ 30) = three monthly
// payments, matching the IV illustration's "three payments for the 90-day
// elimination period". They DIVERGE on an asymmetric claim (home 0 / facility
// 90): the paid home-care days burn the facility bucket while being paid in
// full, so `initial − close` over-counts them; the withheld-day tally does not.
//
// For a client already part-way through their EP at projection start, only the
// days served here are counted, not the days served before the projection began.
// The carrier would reimburse the whole EP, but the pre-projection days were
// never charged as out-of-pocket in our numbers either, so reimbursing them
// would credit cash against cost we never modelled.
function computeEpRetroactiveLump(input: EpLumpInputs): EpLumpResult {
  const {
    ltcPolicy,
    openRelevantWp,
    closeRelevantWp,
    unpaidWpDaysServed,
    baseAfterDraw,
    eobAfterDraw,
    baseMonthlyCap,
    eobMonthlyCap,
    alreadyReimbursed,
  } = input;

  const empty: EpLumpResult = {
    epLumpSumPaid: 0,
    epLumpSumBasePaid: 0,
    epLumpSumEobPaid: 0,
    epLumpSumDays: 0,
    epLumpSumDailyRate: 0,
    fired: false,
  };

  const epJustSatisfied = openRelevantWp > 0 && closeRelevantWp === 0;
  if (!hasRetroactiveEliminationPeriod(ltcPolicy) || !epJustSatisfied || alreadyReimbursed) {
    return empty;
  }

  // Past this point the EP has been satisfied on this row, so the latch MUST
  // set even if nothing can be funded (exhausted pool, zero days served).
  // Otherwise a later bucket satisfying — possible when the home and facility
  // buckets are seeded unequally — would re-enter and fire a second lump.
  // CM II latches the same way, on satisfaction rather than on amount.
  const satisfiedButUnfunded: EpLumpResult = { ...empty, fired: true };

  // Reimburse the days actually withheld from payment (accumulated by the
  // caller), never the raw bucket decrement. Zero withheld days — e.g. a
  // facility EP satisfied entirely by paid, zero-WP home-care days — funds
  // nothing but still latches so the EP is not re-satisfied later.
  const daysServed = Math.max(unpaidWpDaysServed, 0);
  if (daysServed <= 0) {
    return satisfiedButUnfunded;
  }

  const baseDaily = baseMonthlyCap / DAYS_IN_MONTH;
  const eobDaily = eobMonthlyCap / DAYS_IN_MONTH;

  const baseDaysFunded = baseDaily > 0 ? Math.min(daysServed, baseAfterDraw / baseDaily) : 0;
  const epLumpSumBasePaid = Math.min(baseDaily * baseDaysFunded, baseAfterDraw);

  const remainingDays = daysServed - baseDaysFunded;
  const eobDaysFunded = eobDaily > 0 ? Math.min(remainingDays, eobAfterDraw / eobDaily) : 0;
  const epLumpSumEobPaid = Math.min(eobDaily * eobDaysFunded, eobAfterDraw);

  const daysFunded = baseDaysFunded + eobDaysFunded;
  if (daysFunded <= 0) {
    return satisfiedButUnfunded;
  }

  return {
    epLumpSumPaid: epLumpSumBasePaid + epLumpSumEobPaid,
    epLumpSumBasePaid,
    epLumpSumEobPaid,
    epLumpSumDays: daysFunded,
    // Blended across whichever pools actually funded the days. The two caps are
    // equal on this product today, but they are kept as separate variables for
    // shape parity with the two-pool parents — deriving the rate from the mix
    // keeps this correct if they ever diverge.
    epLumpSumDailyRate: (epLumpSumBasePaid + epLumpSumEobPaid) / daysFunded,
    fired: true,
  };
}

// Sub-microdollar FP residue left behind by an exhausting draw must not keep a
// pool "open" — a revived pool would earn phantom inflation accruals at later
// anniversaries. Close-of-month balances below the tolerance snap to exactly 0.
const POOL_EXHAUSTION_TOLERANCE = 1e-6;
const snapExhaustedPool = (balance: number): number =>
  balance < POOL_EXHAUSTION_TOLERANCE ? 0 : balance;

export const secureCareStrategy: PolicyProjectionStrategy<
  SecureCareState,
  SecureCareDeltas
> = {
  initClaimState(client, ltcPolicy): MonthlyState<SecureCareState> {
    const { baseAtIssue, eobAtIssue } = deriveAtIssuePools(ltcPolicy);
    // The death benefit opens from the STORED Face (the ledger's flat Death
    // Benefit column), not the duration-derived base pool — the two coincide by
    // product design but the Face is authoritative, and proportional depletion
    // links DB↔base by ratio, so they need not be equal.
    const faceAmount = ltcPolicy.policyMaximumBenefitAmount ?? 0;
    const utilizedToDate = ltcPolicy.policyBenefitUtilizedToDate ?? 0;
    const minGuaranteed = ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0;
    const { rate, capYears, isSimple } = readInflation(ltcPolicy);
    // Anniversaries between policy issue and claim start are not iterated (the
    // projection only spans claim months), so pre-inflate each pool to its value
    // at claim start. A claim starting in policy year 1 keeps the factor at 1.0.
    // Both pools share the one rate. The Face is static (never pre-inflated).
    const claimStartPolicyYear = getCalendarPolicyYear(client as Client, 0);
    const factor = growthFactor(rate, capYears, isSimple, claimStartPolicyYear);
    const baseAtClaimStart = baseAtIssue * factor;
    const eobAtClaimStart = eobAtIssue * factor;
    const { priorBaseUtilization, priorEobUtilization } = splitPriorUtilization(
      utilizedToDate,
      baseAtClaimStart,
      eobAtClaimStart,
    );
    return {
      pools: {
        baseBenefitPool: Math.max(baseAtClaimStart - priorBaseUtilization, 0),
        extensionOfBenefitsPool: Math.max(eobAtClaimStart - priorEobUtilization, 0),
        // Face opens net of TOTAL prior utilization, floored at the residual —
        // a partially used policy opens reduced.
        deathBenefit: Math.max(faceAmount - utilizedToDate, minGuaranteed),
      },
      waitingPeriod: {
        homeDays:
          ltcPolicy.homeCareWaitingPeriodDaysRemaining ??
          ltcPolicy.homeCareWaitingPeriodDays ??
          0,
        facilityDays:
          ltcPolicy.facilityCareWaitingPeriodDaysRemaining ??
          ltcPolicy.facilityCareWaitingPeriodDays ??
          0,
      },
      strategyState: { indemnitySurplus: 0, epReimbursed: false, epUnpaidWpDaysServed: 0 },
    };
  },

  buildClaimTerms(client, ltcPolicy): ClaimTerms {
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const dailyBenefit = baseMonthly / DAYS_IN_MONTH;
    const { rate, capYears, isSimple } = readInflation(ltcPolicy);
    const clientAsClient = client as Client;
    const projectionAnchorYear = getCareCostAnchorYear(clientAsClient);
    const { baseAtIssue, eobAtIssue, usesDurationModel, hasMixedModelFields } =
      deriveAtIssuePools(ltcPolicy);
    // Prior utilization drains the pre-inflated Acceleration pool first, spilling
    // into Extension (mirrors `initClaimState`), inflated to the SAME claim-start
    // anchor the state seeding used so "drawn to date" matches the deduction.
    const claimStartPolicyYear = getCalendarPolicyYear(clientAsClient, 0);
    const factor = growthFactor(rate, capYears, isSimple, claimStartPolicyYear);
    const { priorBaseUtilization, priorEobUtilization } = splitPriorUtilization(
      ltcPolicy.policyBenefitUtilizedToDate ?? 0,
      baseAtIssue * factor,
      eobAtIssue * factor,
    );
    const priorUtilization = priorBaseUtilization + priorEobUtilization;
    return {
      strategyId: 'secure-care',
      priorUtilization,
      nominalDailyBenefit: dailyBenefit,
      nominalDailyBenefitBase: dailyBenefit,
      // Resolve every environment through the same helper the payout math uses so
      // the displayed cap matches the math. Under cash indemnity SecureCare has a
      // single unified monthly cap, so these are typically identical.
      nominalDailyBenefitByEnvironment: {
        [CareEnvironment.home]: getDailyBenefitAmountForEnvironment(ltcPolicy, CareEnvironment.home),
        [CareEnvironment.assistedLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.assistedLiving,
        ),
        [CareEnvironment.fullCareFacility]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.fullCareFacility,
        ),
        [CareEnvironment.independentLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.independentLiving,
        ),
      },
      // One rate drives both pools and the monthly cap. Surfaced on both the base
      // and EOB knobs so the UI renders a single uniformly-inflating pool.
      inflationRate: rate,
      inflationCapYears: capYears,
      inflationIsSimple: isSimple,
      baseBenefitPoolInflationRate: rate,
      baseBenefitPoolInflationCapYears: capYears,
      extensionOfBenefitsPoolInflationRate: rate,
      extensionOfBenefitsPoolInflationCapYears: capYears,
      baseBenefitPoolAtIssue: baseAtIssue,
      extensionOfBenefitsPoolAtIssue: eobAtIssue,
      // Only surface the durations when the duration-derived model is active
      // (all-or-nothing). Legacy / partially-migrated policies leave these
      // undefined so the UI does not show a misleading "× N months" derivation.
      baseBenefitPoolDurationMonths: usesDurationModel
        ? ltcPolicy.policyAccelerationBenefitDurationMonths ?? undefined
        : undefined,
      extensionOfBenefitsPoolDurationMonths: usesDurationModel
        ? ltcPolicy.policyEobBenefitDurationMonths ?? undefined
        : undefined,
      careCostInflationRate: appliedHealthCareInflation(clientAsClient),
      projectionAnchorYear,
      phaseStartYears: { ...(clientAsClient.clientPhasePredictedStartYears ?? {}) } as Record<
        CarePhase,
        number | null
      >,
      phaseCareMonthsByPhase: getPhaseCareMonthsByPhase(clientAsClient),
      firstPhaseStartYear: getFirstContributingPhaseStartYear(clientAsClient),
      policyPremiumStartYear: ltcPolicy.policyPremiumStartYear ?? projectionAnchorYear,
      minimumGuaranteedDeathBenefit: ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0,
      // SecureCare is ALWAYS cash indemnity — hard-coded, not read from
      // `isIndemnityPolicyPayment`.
      isReimbursement: false,
      isIndemnity: true,
      ...(hasMixedModelFields ? { dataWarnings: [SECURE_CARE_MIXED_MODEL_WARNING] } : {}),
    };
  },

  buildYearSnapshot(input: BuildYearSnapshotInput): YearSnapshot {
    const { ltcPolicy, policyYear } = input;
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const { rate, capYears, isSimple } = readInflation(ltcPolicy);

    // One rate for both pools + the monthly cap; simple OR compound.
    const factor = growthFactor(rate, capYears, isSimple, policyYear);

    const { baseAtIssue, eobAtIssue, usesDurationModel } = deriveAtIssuePools(ltcPolicy);
    const inflatedMonthly = baseMonthly * factor;
    const inflatedBase = baseAtIssue * factor;
    const inflatedEob = eobAtIssue * factor;
    // The Face is STATIC — it never inflates up (matches the flat Death Benefit
    // ledger column).
    const faceAmount = ltcPolicy.policyMaximumBenefitAmount ?? 0;

    // Forward-summed total a claim starting this year would pay across the whole
    // benefit period (the monthly benefit keeps inflating during the claim). Only
    // the duration model has month counts to sum over; the helper honors the
    // simple-vs-compound election, so both elections report a forward total.
    const forwardLtcBenefitTotal = usesDurationModel
      ? forwardBenefitPeriodTotal(ltcPolicy, policyYear, baseMonthly, rate, rate, capYears, isSimple)
      : undefined;

    return {
      policyYear,
      effectiveDailyBenefit: inflatedMonthly / DAYS_IN_MONTH,
      effectiveDailyBenefitBase: inflatedMonthly / DAYS_IN_MONTH,
      effectiveDailyBenefitRider: inflatedMonthly / DAYS_IN_MONTH,
      effectiveFaceAmount: faceAmount,
      effectiveContinuationBenefit: inflatedEob,
      effectiveTotalLtcBenefit: inflatedBase + inflatedEob,
      monthlyBenefitCap: inflatedMonthly,
      // One rate → the continuation monthly cap equals the base cap.
      continuationMonthlyBenefitCap: inflatedMonthly,
      theoreticalLtcPoolCeiling: inflatedBase + inflatedEob,
      ...(forwardLtcBenefitTotal !== undefined ? { forwardLtcBenefitTotal } : {}),
    };
  },

  computeMonth(
    input: MonthComputeInput<SecureCareState, SecureCareDeltas>,
  ): MonthlyEntry<SecureCareState, SecureCareDeltas> {
    const { client, ltcPolicy, phaseCarePlan, monthlyCareInput, openState, priorMonth } = input;
    const { monthIndex, carePhase, monthsIntoPhase } = monthlyCareInput;
    const clientAsClient = client as Client;

    // Cost metrics + structural policy year, resolved up front so the pre-policy
    // gate runs before any WP/ADL classification.
    const costMetrics = computeMonthlyCostMetrics(clientAsClient, phaseCarePlan, monthsIntoPhase);
    const claimYearOffset = Math.floor(monthIndex / 12);
    const policyYear = getCalendarPolicyYear(clientAsClient, monthIndex);

    const context: MonthContext = {
      monthIndex,
      policyYear,
      clientAge: calculateClientAge(clientAsClient.intakeSurvey?.clientAge, policyYear),
      claimYearOffset,
      carePhase,
      monthsIntoPhase,
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
    };

    // Pre-policy gate — a care month before the premium-start year pays $0, full
    // OOP. Single-life floors its policy year at 1, so this is defensive.
    if (policyYear < 1) {
      const prePolicyFacts: MonthlyCareFacts = {
        nominalCareCost: costMetrics.nominalCareCost,
        effectiveCareCost: costMetrics.effectiveCareCost,
        dailyCareCost: costMetrics.dailyCareCost,
        careCostInflationYears: costMetrics.careCostInflationYears,
        projectedCareYear: costMetrics.projectedCareYear,
        non2AdlDays: 0,
        coverageEligibleDays: 0,
        waitingPeriodDaysConsumedHome: 0,
        waitingPeriodDaysConsumedFacility: 0,
        appliedMonthlyBenefitCap: 0,
        appliedCapPoolSource: 'base',
      };
      return buildPrePolicyMonth<SecureCareState, SecureCareDeltas>({
        context,
        facts: prePolicyFacts,
        openState,
        strategyDeltas: {
          indemnitySurplusAccrued: 0,
          indemnitySurplusApplied: 0,
          epLumpSumPaid: 0,
          epLumpSumBasePaid: 0,
          epLumpSumEobPaid: 0,
          epLumpSumDays: 0,
          epLumpSumDailyRate: 0,
          epLumpSumAppliedToCost: 0,
        },
      });
    }

    // 1. Day classification (2-ADL bucket → WP bucket for this care environment
    //    → eligible).
    const non2AdlDays = computeNon2AdlDaysThisRow(clientAsClient, phaseCarePlan, monthsIntoPhase);
    const daysAfter2Adl = DAYS_IN_MONTH - non2AdlDays;

    const wpConsumption = consumeWaitingPeriodDaysForEnvironment(
      openState.waitingPeriod,
      phaseCarePlan.appliedCareEnvironment,
      daysAfter2Adl,
    );
    const {
      wpDaysThisRow,
      closeHomeWp,
      closeFacilityWp,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      openRelevantWp,
      closeRelevantWp,
    } = wpConsumption;
    const eligibleDays = daysAfter2Adl - wpDaysThisRow;

    // 2. Per-pool monthly dollar caps for this policy year. One rate → the two
    //    caps coincide (kept as separate variables for shape parity with the
    //    two-pool parents).
    const { rate, capYears, isSimple } = readInflation(ltcPolicy);
    const baseMonthly = getMonthlyBenefitAmountForEnvironment(
      ltcPolicy,
      phaseCarePlan.appliedCareEnvironment,
    );
    const factor = growthFactor(rate, capYears, isSimple, policyYear);
    const baseMonthlyCap = baseMonthly * factor;
    const eobMonthlyCap = baseMonthly * factor;

    // 3. Annual anniversary accrual against the AT-ISSUE pool amount, gated on
    //    "pool still > 0" so an exhausted pool stays exhausted. The bare `> 0`
    //    gates are reliable because close-of-month balances snap FP residue to 0.
    let baseInflationAccrual = 0;
    let eobInflationAccrual = 0;
    if (priorMonth && policyYear !== priorMonth.context.policyYear) {
      const priorYear = priorMonth.context.policyYear;
      const { baseAtIssue, eobAtIssue } = deriveAtIssuePools(ltcPolicy);
      const factorDelta =
        growthFactor(rate, capYears, isSimple, policyYear) -
        growthFactor(rate, capYears, isSimple, priorYear);
      if (openState.pools.baseBenefitPool > 0) {
        baseInflationAccrual = baseAtIssue * factorDelta;
      }
      if (openState.pools.extensionOfBenefitsPool > 0) {
        eobInflationAccrual = eobAtIssue * factorDelta;
      }
    }
    const baseAfterAccrual = openState.pools.baseBenefitPool + baseInflationAccrual;
    const eobAfterAccrual = openState.pools.extensionOfBenefitsPool + eobInflationAccrual;

    // 4. Indemnity payout — pay the full pro-rated monthly cap as cash regardless
    //    of actual cost, drawn base-then-Extension. The cap follows whichever pool
    //    funds the day. A pool's last paying month may pay less than its cap when
    //    it can't cover it. When both pools are exhausted (or the row is WP-only /
    //    ineligible) nothing is drawn — but the surplus drain below still runs.
    const eligibleCost = costMetrics.dailyCareCost * eligibleDays;

    let drawFromBase = 0;
    let drawFromEob = 0;
    let indemnityPaid = 0;

    if (eligibleDays > 0 && baseAfterAccrual + eobAfterAccrual > 0) {
      const baseProRatedCap = baseMonthlyCap * (eligibleDays / DAYS_IN_MONTH);
      const baseDailyCap = baseMonthlyCap / DAYS_IN_MONTH;
      const eobDailyCap = eobMonthlyCap / DAYS_IN_MONTH;

      const baseDaysFunded =
        baseDailyCap > 0 ? Math.min(eligibleDays, baseAfterAccrual / baseDailyCap) : 0;
      drawFromBase = Math.min(baseDailyCap * baseDaysFunded, baseProRatedCap, baseAfterAccrual);

      const remainingDays = eligibleDays - baseDaysFunded;
      const eobDaysFunded =
        eobDailyCap > 0 ? Math.min(remainingDays, eobAfterAccrual / eobDailyCap) : 0;
      drawFromEob = Math.min(eobDailyCap * eobDaysFunded, eobAfterAccrual);

      indemnityPaid = drawFromBase + drawFromEob;
    }

    // Gross cash this row pays out across both pools. The pools deplete by this
    // gross cash; the surplus reserve is a separate accounting layer on top.
    const rowPayout = drawFromBase + drawFromEob;
    const policyExhausted = baseAfterAccrual + eobAfterAccrual <= 0;

    // 4b. Indemnity-surplus accounting (per-row), called UNCONDITIONALLY. Cash
    //     above eligible cost banks as surplus and drains on later shortfall rows
    //     WHILE the policy is still live; once both pools are spent the reserve
    //     FREEZES (it must not bleed into post-exhaustion out-of-pocket cost).
    const surplusResult = applyIndemnitySurplus({
      openSurplus: openState.strategyState.indemnitySurplus,
      indemnityPayout: rowPayout,
      eligibleCost,
      fullCost: costMetrics.effectiveCareCost,
      // Two pools: exhausted only when BOTH are spent.
      policyExhausted,
    });
    const ltcCoveragePaid = surplusResult.ltcCoveragePaid;

    // 4c. Retroactive EP lump (SecureCare IV only) — fires on the row where the
    //     active environment's WP bucket empties, once per claim. Drawn from what
    //     the regular draw left, Acceleration first then Extension.
    const unpaidWpDaysServed = openState.strategyState.epUnpaidWpDaysServed + wpDaysThisRow;
    const epResult = computeEpRetroactiveLump({
      ltcPolicy,
      openRelevantWp,
      closeRelevantWp,
      unpaidWpDaysServed,
      baseAfterDraw: baseAfterAccrual - drawFromBase,
      eobAfterDraw: eobAfterAccrual - drawFromEob,
      baseMonthlyCap,
      eobMonthlyCap,
      alreadyReimbursed: openState.strategyState.epReimbursed,
    });

    // The lump first covers whatever cost this row's regular coverage left
    // unpaid; anything above that banks as indemnity surplus rather than riding
    // as a negative out-of-pocket credit (mirrors cmTwoStrategy).
    const costAfterCoverage = Math.max(
      costMetrics.effectiveCareCost - surplusResult.ltcPayoutToCost,
      0,
    );
    const epLumpSumAppliedToCost = Math.min(epResult.epLumpSumPaid, costAfterCoverage);
    const epLumpExcess = epResult.epLumpSumPaid - epLumpSumAppliedToCost;
    // Opening surplus may cover WP cost before or alongside reimbursement;
    // surplus accrued on this row remains banked.
    const costAfterCoverageAndLump = Math.max(costAfterCoverage - epLumpSumAppliedToCost, 0);
    const waitingPeriodSurplusApplied = applyOpeningSurplusToCost({
      openSurplus: openState.strategyState.indemnitySurplus,
      alreadyApplied: surplusResult.surplusApplied,
      remainingCost: costAfterCoverageAndLump,
      enabled: wpDaysThisRow > 0,
      policyExhausted,
    });
    const totalSurplusApplied = surplusResult.surplusApplied + waitingPeriodSurplusApplied;
    const closeSurplus =
      surplusResult.closeSurplus - waitingPeriodSurplusApplied + epLumpExcess;
    const surplusAccrued = surplusResult.surplusAccrued + epLumpExcess;

    // 5. Closing balances + PROPORTIONAL death benefit. Only Acceleration
    //    drawdown reduces the Face, and it falls by the same fraction as the
    //    pool (not dollar-for-dollar), so an inflated draw never over-depletes
    //    the flat Face. Floored at the residual; Extension drawdown never touches
    //    the Face. At 0% inflation this coincides with dollar-for-dollar.
    //    The EP lump's Acceleration portion depletes the pool here, so it feeds
    //    the same proportional reduction as a regular draw.
    const closeBase = snapExhaustedPool(
      baseAfterAccrual - drawFromBase - epResult.epLumpSumBasePaid,
    );
    const closeEob = snapExhaustedPool(eobAfterAccrual - drawFromEob - epResult.epLumpSumEobPaid);
    const minGuaranteed = ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0;
    const unflooredCloseDeathBenefit =
      baseAfterAccrual > 0
        ? openState.pools.deathBenefit * (closeBase / baseAfterAccrual)
        : openState.pools.deathBenefit;
    const closeDeathBenefit = Math.max(unflooredCloseDeathBenefit, minGuaranteed);
    const deathBenefitReduction = openState.pools.deathBenefit - closeDeathBenefit;
    const deathBenefitFloored =
      closeDeathBenefit === minGuaranteed && unflooredCloseDeathBenefit < minGuaranteed;

    // A month drawing ANY Acceleration dollars reports the base cap; only months
    // funded purely from Extension report the EOB-rate cap (identical here since
    // one rate, but kept for shape parity + correct pool-source labeling).
    // The EP lump counts as Acceleration funding for labeling purposes: on the
    // firing row `eligibleDays` is normally 0, so both regular draws are 0, and
    // without this the row would be labeled EOB-funded purely because the lump
    // drained the last of the Acceleration pool.
    const drewAnyBase = drawFromBase > 0 || epResult.epLumpSumBasePaid > 0;
    const drewAnyEob = drawFromEob > 0 || epResult.epLumpSumEobPaid > 0;
    const inEobPhase = !drewAnyBase && (drewAnyEob || (closeBase <= 0 && closeEob > 0));

    const facts: MonthlyCareFacts = {
      nominalCareCost: costMetrics.nominalCareCost,
      effectiveCareCost: costMetrics.effectiveCareCost,
      dailyCareCost: costMetrics.dailyCareCost,
      careCostInflationYears: costMetrics.careCostInflationYears,
      projectedCareYear: costMetrics.projectedCareYear,
      non2AdlDays,
      coverageEligibleDays: eligibleDays,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      appliedMonthlyBenefitCap: inEobPhase ? eobMonthlyCap : baseMonthlyCap,
      appliedCapPoolSource: inEobPhase ? 'eob' : 'base',
    };

    // Net out-of-pocket: cost − regular coverage − the lump portion that covered
    // THIS row's cost. The retroactive lump is what makes an EP row payable at
    // all, so on the firing row this is what turns those days from OOP to
    // covered.
    const ineligibleCost =
      costMetrics.effectiveCareCost -
      surplusResult.ltcPayoutToCost -
      epLumpSumAppliedToCost -
      waitingPeriodSurplusApplied;

    const payout: PayoutSummary = {
      ltcCoveragePaid,
      ltcPayoutToCost: surplusResult.ltcPayoutToCost + waitingPeriodSurplusApplied,
      ineligibleCost,
      indemnityPayout: indemnityPaid,
    };

    const deltas: MonthlyDeltas<SecureCareDeltas> = {
      payout,
      base: {
        depletion: drawFromBase + epResult.epLumpSumBasePaid,
        inflationAccrual: baseInflationAccrual,
      },
      eob: {
        depletion: drawFromEob + epResult.epLumpSumEobPaid,
        inflationAccrual: eobInflationAccrual,
      },
      deathBenefit: {
        reduction: deathBenefitReduction,
        flooredAtMinimum: deathBenefitFloored,
      },
      strategyDeltas: {
        indemnitySurplusAccrued: surplusAccrued,
        indemnitySurplusApplied: totalSurplusApplied,
        epLumpSumPaid: epResult.epLumpSumPaid,
        epLumpSumBasePaid: epResult.epLumpSumBasePaid,
        epLumpSumEobPaid: epResult.epLumpSumEobPaid,
        epLumpSumDays: epResult.epLumpSumDays,
        epLumpSumDailyRate: epResult.epLumpSumDailyRate,
        epLumpSumAppliedToCost,
      },
    };

    const closeState: MonthlyState<SecureCareState> = {
      pools: {
        baseBenefitPool: closeBase,
        extensionOfBenefitsPool: closeEob,
        deathBenefit: closeDeathBenefit,
      },
      waitingPeriod: { homeDays: closeHomeWp, facilityDays: closeFacilityWp },
      strategyState: {
        indemnitySurplus: closeSurplus,
        epReimbursed: openState.strategyState.epReimbursed || epResult.fired,
        // Keep accumulating withheld days until the lump has fired; freeze after
        // so the (now-latched) counter can't grow with post-reimbursement care.
        epUnpaidWpDaysServed: openState.strategyState.epReimbursed
          ? openState.strategyState.epUnpaidWpDaysServed
          : unpaidWpDaysServed,
      },
    };

    const status: MonthlyStatus = {
      ltcPoolExhausted: closeBase + closeEob <= 0,
      clientPoolExhausted: closeBase + closeEob <= 0,
      epSatisfied: closeRelevantWp === 0,
    };

    const milestones = deriveStandardMilestones<SecureCareState, SecureCareDeltas>({
      context,
      facts,
      openState,
      closeState,
      deltas,
      priorMonth,
      retroactiveLumpPaid: epResult.epLumpSumPaid,
      minimumGuaranteedDeathBenefit: minGuaranteed,
      clientHasStartedLtc: clientAsClient.intakeSurvey?.clientHasStartedLtc ?? false,
      hasInflationProtection: rate > 0,
    });

    const ineligibilityInput = {
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      facts,
      openState,
      closeState,
      payout,
    };
    const ineligibilityReason = deriveIneligibilityReason<SecureCareState>(ineligibilityInput);
    const ineligibleDayBreakdown =
      deriveIneligibleDayBreakdown<SecureCareState>(ineligibilityInput);

    return {
      context,
      facts,
      openState,
      state: closeState,
      deltas,
      events: { status, milestones, ineligibilityReason, ineligibleDayBreakdown },
    };
  },

  finalizeDeathBenefit({
    closingState,
  }: FinalizeDeathBenefitInput<SecureCareState>): number {
    return closingState.pools.deathBenefit;
  },
};
